import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useDossierStore } from "../../store/dossierStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let mounted = true;

    const routeToAuth = (reason: string) => {
      if (!mounted) return;
      console.warn("[AUTH] ProtectedRoute → /auth:", reason);
      setAuthorized(false);
      setChecking(false);
      navigate("/auth", { replace: true });
    };

    const applyUser = (u: { email?: string | null; email_confirmed_at?: string | null; app_metadata?: { provider?: string } } | null) => {
      if (!mounted) return;
      if (!u) {
        routeToAuth("aucun utilisateur après lecture session (getSession/getUser)");
        return;
      }
      setAuthorized(true);
      setChecking(false);
    };

    void (async () => {
      // 1) Session persistée (localStorage) — fiable au rechargement après login.
      // 2) getUser() peut être lent ou échouer au tout premier tick : ne pas s’y fier seul.
      const readUserFromClient = async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session?.user) return sessionData.session.user;
        const { data: userData } = await supabase.auth.getUser();
        return userData.user ?? null;
      };

      let user: Awaited<ReturnType<typeof readUserFromClient>> = null;
      for (let i = 0; i < 12; i += 1) {
        user = await readUserFromClient();
        if (user) break;
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      applyUser(user);
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "SIGNED_OUT") {
        useDossierStore.getState().clearDossier();
        routeToAuth("SIGNED_OUT");
        return;
      }

      // Ignore les états transitoires sans session.
      if (!session?.user) {
        return;
      }
      applyUser(session.user);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (checking) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <Loader2
          size={40}
          strokeWidth={1.5}
          style={{ color: "#94a3b8", animation: "spin 1s linear infinite" }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!authorized) {
    return null;
  }

  return <>{children}</>;
}
